import { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AlertCircle, KeyRound, Loader2 } from 'lucide-react';

const IS_DEV = import.meta.env.DEV;

const ERROR_MESSAGES: Record<string, string> = {
  no_account: 'No account found for this GitHub user. Please register with an invite code first.',
  suspended: 'Your account has been suspended. Please contact an administrator.',
};

const BACKGROUND_SYMBOLS = [
  'A', 'θ', 'Ω', 'æ', '∫', 'Z',
  'δ', 'E', 'G', 'λ', '∑', 'β',
];

export default function Login() {
  const { isAuthenticated, loginWithApiKey } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const authError = searchParams.get('auth_error');

  // Dev mode API Key state
  const [showDevLogin, setShowDevLogin] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [devError, setDevError] = useState<string | null>(null);
  const [devLoading, setDevLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleGitHubLogin = () => {
    window.location.href = '/api/v1/auth/github';
  };

  const handleDevLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!apiKey.trim()) return;

    setDevLoading(true);
    setDevError(null);

    const success = await loginWithApiKey(apiKey.trim());
    if (success) {
      navigate('/', { replace: true });
    } else {
      setDevError('Invalid API key. Check that the API server is running.');
      setDevLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#f2f4f6] flex flex-col overflow-hidden">
      {/* Decorative background symbols */}
      <div
        className="absolute inset-0 grid grid-cols-6 grid-rows-2 gap-4 p-10 pointer-events-none select-none"
        aria-hidden="true"
      >
        {BACKGROUND_SYMBOLS.map((symbol, i) => (
          <div
            key={i}
            className="flex items-start justify-start font-extrabold text-[60px] text-[#191c1e]"
            style={{ opacity: 0.05 }}
          >
            {symbol}
          </div>
        ))}
      </div>

      {/* Main card — vertically + horizontally centered */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center w-full max-w-[448px] mx-auto px-6 gap-12">
        <div className="w-full bg-white border border-[#e0e3e5] rounded-[12px] shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.05),0px_2px_4px_-2px_rgba(0,0,0,0.05)] px-10 pt-10 pb-10">
          {/* Brand icon */}
          <div className="flex justify-center">
            <div className="w-12 h-12 bg-[#4648d4] rounded-[8px] flex items-center justify-center">
              <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
                <path
                  d="M2 8h4l2-4 4 8 2-4h4"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          {/* Brand name */}
          <h1 className="text-center font-extrabold text-[36px] text-[#191c1e] tracking-[-1.8px] leading-[40px] mt-3">
            Cadence
          </h1>

          {/* Subtitle */}
          <p className="text-center text-[10px] uppercase tracking-[1px] text-[#464554] font-semibold mt-4">
            THE INTELLECTUAL ARCHITECT&apos;S LANGUAGE OS
          </p>

          {/* Description */}
          <p className="text-center text-[14px] text-[#464554] mt-4 leading-relaxed max-w-[320px] mx-auto">
            Access your personalized language workspace
            and continue your mastery journey.
          </p>

          {/* Error banner */}
          {(authError || devError) && (
            <div className="mt-5 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
              <p className="text-sm text-red-700">
                {devError ?? ERROR_MESSAGES[authError!] ?? 'Authentication failed. Please try again.'}
              </p>
            </div>
          )}

          {/* GitHub login button */}
          <button
            onClick={handleGitHubLogin}
            className="mt-8 w-full bg-[#191c1e] hover:bg-[#2a2d30] transition-colors rounded-[8px] py-4 flex items-center justify-center cursor-pointer"
          >
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
            </svg>
            <span className="font-bold text-[16px] text-white ml-3">Login with GitHub</span>
          </button>

          {/* Dev mode API Key login — only visible in local development */}
          {IS_DEV && (showDevLogin ? (
            <form onSubmit={handleDevLogin} className="mt-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="cdk_localdev000..."
                  className="flex-1 bg-[#f2f4f6] rounded-[8px] px-3 py-2.5 font-mono text-[13px] text-[#191c1e] placeholder:text-[#c7c4d7] outline-none focus:ring-2 focus:ring-[#4648d4]/30"
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={!apiKey.trim() || devLoading}
                  className="bg-[#4648d4] hover:bg-[#3a3cb8] text-white rounded-[8px] px-4 py-2.5 text-[13px] font-semibold disabled:opacity-50 transition-colors cursor-pointer"
                >
                  {devLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Connect'}
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => setShowDevLogin(true)}
              className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-[8px] border border-dashed border-[#c7c4d7] text-[#767586] hover:text-[#464554] hover:border-[#464554] transition-colors text-[13px] font-medium cursor-pointer"
            >
              <KeyRound className="w-4 h-4" />
              Dev Mode: API Key Login
            </button>
          ))}

          {/* Divider */}
          <div className="mt-6 pt-2">
            <div className="bg-[rgba(199,196,215,0.5)] h-px" />
          </div>

          {/* Footer links */}
          <div className="flex justify-between mt-5 px-2">
            <span className="font-semibold text-[12px] text-[#464554] cursor-default">
              Need help?
            </span>
            <Link
              to="/register"
              className="font-semibold text-[12px] text-[#464554] hover:text-[#191c1e] transition-colors"
            >
              Create an account
            </Link>
          </div>
        </div>

        {/* Status indicator pill */}
        <div className="flex flex-col items-center gap-3 opacity-80">
          <div className="bg-white border border-[rgba(199,196,215,0.3)] rounded-full px-[13px] py-[7px] flex items-center gap-2 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]">
            <div className="w-2 h-2 rounded-full bg-[#4648d4]" />
            <span className="font-bold text-[10px] uppercase tracking-[2px] text-[#191c1e]">
              Nodes operational
            </span>
          </div>
          <p className="text-center font-medium text-[10px] text-[rgba(70,69,84,0.6)] max-w-[220px]">
            Architecting the bridge between thought and expression since 2024.
          </p>
        </div>
      </div>

      {/* Global footer */}
      <div className="relative z-10 w-full max-w-[1280px] flex justify-between items-center mt-auto px-10 py-8">
        <span className="font-semibold text-[11px] text-[rgba(70,69,84,0.5)] uppercase tracking-[0.55px]">
          &copy; 2024 Cadence OS.
        </span>
        <div className="flex gap-8">
          <span className="font-semibold text-[11px] text-[rgba(70,69,84,0.5)] uppercase tracking-[0.55px]">
            Privacy Policy
          </span>
          <span className="font-semibold text-[11px] text-[rgba(70,69,84,0.5)] uppercase tracking-[0.55px]">
            Terms of Service
          </span>
          <span className="font-semibold text-[11px] text-[rgba(70,69,84,0.5)] uppercase tracking-[0.55px]">
            Support
          </span>
        </div>
      </div>
    </div>
  );
}
